/**
 * "Who's free on Friday?" — one day, a bar per person cut into booked,
 * pencilled and free stretches across the business's working window, and one
 * sentence per person saying when they have room
 * (docs/intelligent-chat-answers-plan.md, "Availability strip").
 *
 * 🚨 The window is the BUSINESS's hours for that day, never the person's.
 * Per-engineer working hours are not modelled anywhere in Mucka (see
 * `src/lib/schedule/free-time.ts`), so every sentence here says "within your
 * business hours" and the strip never claims anybody IS available — only that
 * nothing is booked. An engineer who does not work Fridays still shows free.
 *
 * Built from the schedule the server read, through the shared gap finder
 * (`workingWindow`, `daySegments`, `freeRuns`). There is deliberately no second
 * gap finder here: the slot picker reads the same three functions, so the two
 * components cannot disagree about when somebody is free.
 *
 * Pure and directive-free (anti-pattern #18): the route, the panel, the Mucka
 * Bar and the oversight grader all import it.
 */
import type { BusinessHours, BusinessHoursSource } from '@/lib/business-hours/hours';
import type { TimelineBlock } from '@/lib/schedule/day-timeline';
import { daySegments, freeMinutes, freeRuns, workingWindow, type DaySegment, type DayWindow } from '@/lib/schedule/free-time';
import { londonTodayIso } from '@/lib/on-time/local-time';

export interface AvailabilityPerson {
  /** `public.users.id`. */
  userId: string;
  /** "You", or a first name (with a last initial when two share one). */
  name: string;
  isViewer: boolean;
  /** The working window cut into stretches, in order, covering it exactly. */
  segments: DaySegment[];
  freeMinutes: number;
  /** What a tap on the row says, built here so every surface says the same. */
  sentence: string;
}

export interface AvailabilityStripBlock {
  kind: 'availability-strip';
  /** YYYY-MM-DD, London. */
  date: string;
  /** "Friday 16 October". */
  dayLabel: string;
  /** "Friday". */
  weekday: string;
  /**
   * `open` — there is a window to show. `shut` — the business hours have the
   * day closed. `over` — it is today and the working day has already ended.
   */
  state: 'open' | 'shut' | 'over';
  /** The business's hours that day; null when shut. */
  hours: DayWindow | null;
  /** What the bars cover: the hours, or from now on when the day is today. */
  window: DayWindow | null;
  /** True when the window starts at the current time (the day is today). */
  fromNow: boolean;
  /** 'default' means nobody has set business hours: Mon–Fri 08:00–17:00 is assumed. */
  hoursSource: BusinessHoursSource;
  people: AvailabilityPerson[];
  /** People not shown (they had less free time than the ones shown). */
  more: number;
  /** Jobs that day with nobody on them; null when the seat cannot see the whole diary. */
  unassigned: number | null;
  /** The seat sees only its own diary (no `view_all_jobs` / `view_team`). */
  ownOnly: boolean;
  /** The row shown first in the detail strip. */
  focusUserId: string | null;
  /** "14:05", London — when this was read, because a stored reply is a snapshot. */
  asOf: string;
  /** Where "Open Friday on the schedule" goes. */
  path: string;
}

/** Most rows the strip draws; the rest are counted, never dropped silently. */
export const AVAILABILITY_MAX_PEOPLE = 10;

/** The shortest gap the sentence calls free time. */
export const MIN_FREE_MINUTES = 30;

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
// Spelled out rather than formatted: Node and WebKit disagree about short
// month names (anti-pattern #41), and a stored block is drawn by both.
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** "09:15" for minutes from midnight. */
export function clock(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function dayParts(dayIso: string): { weekday: string; label: string; weekdayIndex: number } {
  const d = new Date(`${dayIso}T12:00:00Z`);
  const weekdayIndex = d.getUTCDay();
  const weekday = WEEKDAYS[weekdayIndex];
  return { weekday, weekdayIndex, label: `${weekday} ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}` };
}

/** Where the schedule opens on one day (`schedule/page.tsx` reads `view` + `day`). */
export function scheduleDayPath(dayIso: string): string {
  return `/schedule?view=today&day=${dayIso}`;
}

/**
 * The London day a lookup's date range covers, when it covers exactly one —
 * the hook's test for "this was a question about one day". Read at the
 * range's midpoint so a window built on UTC midnights (the named `today` /
 * `tomorrow` ranges) and one built on London midnights land on the same day.
 */
export function availabilityDayOf(start: Date | null, end: Date | null): string | null {
  if (!start || !end) return null;
  const span = end.getTime() - start.getTime();
  if (!(span > 0) || span > 25 * 60 * 60 * 1000) return null;
  return londonTodayIso(new Date(start.getTime() + span / 2));
}

/** One person's day, as the server read it. */
export interface AvailabilityPersonInput {
  userId: string;
  /** Their full name as stored. */
  name: string;
  isViewer: boolean;
  timed: readonly TimelineBlock[];
  allDay: readonly TimelineBlock[];
}

export interface AvailabilityInput {
  dayIso: string;
  /** London's date now. */
  todayIso: string;
  /** London minutes past midnight now. */
  nowMin: number;
  hours: BusinessHours;
  hoursSource: BusinessHoursSource;
  people: readonly AvailabilityPersonInput[];
  unassigned: number | null;
  ownOnly: boolean;
}

/**
 * "You", first names, and a last initial where two people share a first name
 * — the strip has 60px for a name, and "Dave" twice is no answer at all.
 */
export function shortNames(people: readonly Pick<AvailabilityPersonInput, 'userId' | 'name' | 'isViewer'>[]): Map<string, string> {
  const out = new Map<string, string>();
  const first = (n: string) => n.trim().split(/\s+/)[0] || 'Unnamed';
  const counts = new Map<string, number>();
  for (const p of people) if (!p.isViewer) counts.set(first(p.name).toLowerCase(), (counts.get(first(p.name).toLowerCase()) ?? 0) + 1);
  for (const p of people) {
    if (p.isViewer) { out.set(p.userId, 'You'); continue; }
    const f = first(p.name);
    if ((counts.get(f.toLowerCase()) ?? 0) < 2) { out.set(p.userId, f); continue; }
    const parts = p.name.trim().split(/\s+/);
    out.set(p.userId, parts.length > 1 ? `${f} ${parts[parts.length - 1][0]}` : p.name.trim());
  }
  return out;
}

function joinAnd(parts: string[]): string {
  if (parts.length <= 1) return parts.join('');
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/**
 * The sentence a tap on a person shows. Free stretches of half an hour or
 * more, then anything pencilled, always ending on the business-hours caveat.
 */
export function freeSentence(segments: readonly DaySegment[], window: DayWindow, opts: { allDay: boolean; fromNow: boolean }): string {
  const caveat = 'within your business hours';
  if (opts.allDay) {
    return segments.every(s => s.kind === 'pencilled')
      ? `An all-day entry is pencilled in, so no free time ${caveat} until it is settled.`
      : `An all-day entry fills the day, so no free time ${caveat}.`;
  }
  const runs = freeRuns(segments, MIN_FREE_MINUTES);
  const pencilled = segments.filter(s => s.kind === 'pencilled');
  const pencilledNote = pencilled.length
    ? ` Pencilled, not confirmed: ${joinAnd(pencilled.map(s => `${clock(s.startMin)} to ${clock(s.endMin)}`))}.`
    : '';

  if (runs.length === 1 && runs[0].startMin === window.startMin && runs[0].endMin === window.endMin) {
    return opts.fromNow
      ? `Nothing booked for the rest of the day. Free from now until ${clock(window.endMin)}, ${caveat}.`
      : `Nothing booked. Free all day, ${caveat}.`;
  }
  if (!runs.length) {
    const any = freeMinutes(segments) > 0;
    return (any
      ? `No free stretch of half an hour or more ${caveat}.`
      : `Booked for the whole of ${opts.fromNow ? 'the rest of the day' : 'the day'}, ${caveat}.`) + pencilledNote;
  }
  const phrases = runs.map(r => {
    if (r.startMin === window.startMin) return opts.fromNow ? `from now until ${clock(r.endMin)}` : `until ${clock(r.endMin)}`;
    if (r.endMin === window.endMin) return `after ${clock(r.startMin)}`;
    return `${clock(r.startMin)} to ${clock(r.endMin)}`;
  });
  return `Free ${joinAnd(phrases)}, ${caveat}.${pencilledNote}`;
}

/** Round up to the next quarter hour, so "from now" reads 14:15, not 14:07. */
function nextQuarter(min: number): number {
  return Math.ceil(min / 15) * 15;
}

/**
 * The strip, or null when the day is in the past (a free-time strip about a day
 * that has gone answers nothing) or there is nobody to show.
 */
export function availabilityStripBlock(input: AvailabilityInput): AvailabilityStripBlock | null {
  const { dayIso, todayIso } = input;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dayIso) || dayIso < todayIso) return null;
  const { weekday, label } = dayParts(dayIso);
  const base = {
    kind: 'availability-strip' as const,
    date: dayIso,
    dayLabel: label,
    weekday,
    hoursSource: input.hoursSource,
    unassigned: input.ownOnly ? null : input.unassigned,
    ownOnly: input.ownOnly,
    asOf: clock(input.nowMin),
    path: scheduleDayPath(dayIso),
  };

  const hours = workingWindow(input.hours, dayIso);
  if (!hours) {
    return { ...base, state: 'shut', hours: null, window: null, fromNow: false, people: [], more: 0, focusUserId: null };
  }
  if (!input.people.length) return null;

  const isToday = dayIso === todayIso;
  const startMin = isToday ? Math.max(hours.startMin, nextQuarter(input.nowMin)) : hours.startMin;
  if (startMin >= hours.endMin) {
    return { ...base, state: 'over', hours, window: null, fromNow: false, people: [], more: 0, focusUserId: null };
  }
  const window: DayWindow = { startMin, endMin: hours.endMin };
  const fromNow = startMin > hours.startMin;

  const names = shortNames(input.people);
  const all: AvailabilityPerson[] = input.people.map(p => {
    const segments = daySegments(p.timed, window, p.allDay);
    return {
      userId: p.userId,
      name: names.get(p.userId) ?? p.name,
      isViewer: p.isViewer,
      segments,
      freeMinutes: p.allDay.length ? 0 : freeMinutes(segments),
      sentence: freeSentence(segments, window, { allDay: p.allDay.length > 0, fromNow }),
    };
  });

  // Over the cap, keep the people with the most room (the question is who has
  // room), but draw them in the roster's order so the list reads like a team.
  let people = all;
  if (all.length > AVAILABILITY_MAX_PEOPLE) {
    const keep = new Set(
      [...all].sort((a, b) => b.freeMinutes - a.freeMinutes || all.indexOf(a) - all.indexOf(b))
        .slice(0, AVAILABILITY_MAX_PEOPLE)
        .map(p => p.userId),
    );
    people = all.filter(p => keep.has(p.userId));
  }
  // The viewer first, the way the diary has them.
  people = [...people.filter(p => p.isViewer), ...people.filter(p => !p.isViewer)];

  const roomiest = people.reduce<AvailabilityPerson | null>((best, p) => (!best || p.freeMinutes > best.freeMinutes ? p : best), null);
  return {
    ...base,
    state: 'open',
    hours,
    window,
    fromNow,
    people,
    more: all.length - people.length,
    focusUserId: roomiest?.userId ?? null,
  };
}

/** Labels along the hours axis: both ends and a few whole hours between. */
export function axisTicks(window: DayWindow): { minutes: number; at: number }[] {
  const span = window.endMin - window.startMin;
  if (span <= 0) return [];
  const stepHours = Math.max(1, Math.ceil(span / 60 / 3));
  const ticks = [window.startMin];
  for (let t = Math.ceil(window.startMin / 60) * 60; t < window.endMin; t += stepHours * 60) {
    if (t - ticks[ticks.length - 1] >= stepHours * 30 && window.endMin - t >= stepHours * 30) ticks.push(t);
  }
  ticks.push(window.endMin);
  return ticks.map(minutes => ({ minutes, at: (minutes - window.startMin) / span }));
}

/** "Fridays" — for "your business hours on Fridays". */
export function weekdayPlural(weekday: string): string {
  return `${weekday}s`;
}

/**
 * One line for the oversight grader: counts and states, no names (a person
 * in the team is not a customer, but the grader has no need of them either).
 */
export function summariseAvailabilityStripBlock(block: AvailabilityStripBlock): string {
  if (block.state === 'shut') return `[availability-strip: ${block.date}, business shut that day per its hours]`;
  if (block.state === 'over') return `[availability-strip: ${block.date}, working day already over]`;
  const shown = block.people.length;
  const total = shown + block.more;
  const withRoom = block.people.filter(p => freeRuns(p.segments, MIN_FREE_MINUTES).length > 0).length;
  const w = block.window!;
  return `[availability-strip: ${block.date}, ${total} ${total === 1 ? 'person' : 'people'}${block.more ? ` (${shown} shown)` : ''}, `
    + `${withRoom} with free time, ${clock(w.startMin)}–${clock(w.endMin)} business hours${block.hoursSource === 'default' ? ' (default, not set)' : ''}`
    + `${block.ownOnly ? ', own diary only' : ''}]`;
}

/**
 * What the model is told the strip says, appended to the tool result so the
 * reply's sentence carries the same facts the bars do (the block is extra, the
 * words are the answer) and the model does not work free time out for itself.
 */
export function availabilityResultNote(block: AvailabilityStripBlock): string {
  const onScreen = `It is on screen as an availability strip for ${block.dayLabel}.`;
  if (block.state === 'shut') {
    return `${onScreen} The business is SHUT on ${weekdayPlural(block.weekday)} according to its business hours, so there is no free time to offer that day. Say so in one sentence.`;
  }
  if (block.state === 'over') {
    return `${onScreen} The business's working day has already finished today (its hours end at ${clock(block.hours!.endMin)}). Say so; do not offer time today.`;
  }
  const w = block.window!;
  const lines = block.people.map(p => `${p.name}: ${p.sentence}`).join(' ');
  return `Free time on ${block.dayLabel}, ${block.fromNow ? 'from now ' : ''}within the business's hours of ${clock(w.startMin)} to ${clock(w.endMin)}. `
    + `Mucka does not know anybody's own working hours, so say what is booked and free, never that someone IS available. ${lines}`
    + (block.more ? ` ${block.more} more ${block.more === 1 ? 'person has' : 'people have'} less free time and ${block.more === 1 ? 'is' : 'are'} not shown.` : '')
    + (block.unassigned ? ` ${block.unassigned} job${block.unassigned === 1 ? '' : 's'} that day ${block.unassigned === 1 ? 'has' : 'have'} nobody on ${block.unassigned === 1 ? 'it' : 'them'} yet.` : '')
    + (block.ownOnly ? ' Only the asker\'s own diary is shown; do not describe anybody else\'s day.' : '')
    + ` ${onScreen} Answer in a sentence or two (who has room, and when) and do not read every person out.`;
}
